import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppInput from "../../components/Core/AppInput/AppInput";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import { DocumentBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../components/Core/Button/Button";
import { useDocuments, useDeleteDocument } from "../../networking/organisation.hooks";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import useQueryState from "../../hooks/useQueryState";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import Pagination from "../../components/Shared/Pagination/Pagination";
import { documentEditRoute, documentLinkEditRoute } from "../../utils/route.utils";
import { Alert, AlertDescription, useToast } from "@chakra-ui/react";

const DocumentsPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const toast = useToast();

	const [search, setSearchTerm] = useQueryState<string>("search", { initialValue: "" });
	const [pageNumber, setPageNumber] = useQueryState<number>("page", {
		initialValue: 1,
		valueFromString: (str) => Number(str),
	});
	const {
		data: documentsList,
		isLoading,
		refetch,
	} = useDocuments(true, {
		page: pageNumber,
		pageSize: PAGE_SIZE,
		sort: "metadata.created",
		order: 1,
		keyword: search,
	});

	const {
		mutate: deleteDocument,
		isLoading: deleting,
		isSuccess: deleteSuccess,
		isError: deleteError,
	} = useDeleteDocument();

	useEffect(() => {
		refetch();
	}, [pageNumber, search]);

	useEffect(() => {
		if (deleteSuccess) {
			toast({
				id: "documentDeleted",
				render: () => (
					<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_DELETED_SUCCESS")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
			});
		}
		if (deleteError) {
			toast({
				id: "documentDelete error",
				render: () => (
					<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_DELETE_FAILED")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
			});
		}
	}, [deleteSuccess, deleteError]);

	function handleDocumentEdit(documentId: string) {
		navigate(documentEditRoute(documentId));
	}

	function handleDocumentLinkEdit(documentId: string) {
		navigate(documentLinkEditRoute(documentId));
	}

	function handleDeleteDocument(documentId: string, documentTitle: string) {
		if (window.confirm(`Are you sure you want to delete "${documentTitle}"?`)) {
			deleteDocument(documentId);
		}
	}

	const handleLinkClick = (event: any) => {
		event.stopPropagation(); // Prevent row click when clicking the link
	};

	return (
		<>
			<Breadcrumbs items={DocumentBreadcrumbs} />
			<div className="p-4 px-6">
				<Flex direction={"row"}>
					<AppInput
						containerWidth="200px"
						placeholder={t("SEARCH")}
						icon="search"
						onChangeText={setSearchTerm}
					/>
					<Flex direction={"row"}>
						<Flex justifyContent="flex-end">
							<Flex width={"auto"} justifyContent="space-between" gap={"16px"}>
								<Link to={routes.LINK_UPLOAD}>
									<Button width="100%">{t("ADD_LINK")}</Button>
								</Link>
								<Link to={routes.DOCUMENT_UPLOAD}>
									<Button width="100%">{t("UPLOAD_FILE")}</Button>
								</Link>
							</Flex>
						</Flex>
					</Flex>
				</Flex>
				<div>
					<TableLayout>
						<table>
							<thead>
								<tr>
									<th>{t("TITLE")}</th>
									<th>{t("FILE_OR_LINK")}</th>
									<th>{t("TYPE")}</th>
									<th>{t("ACTIONS")}</th>
								</tr>
							</thead>
							<tbody>
								{documentsList?.documents &&
									Array.isArray(documentsList?.documents) &&
									documentsList?.documents
										?.filter((doc) => {
											if (!search) {
												return doc;
											} else {
												return doc.title.toLowerCase().includes(search.toLowerCase());
											}
										})
										.map((document, index) => {
											{
												return (
													<tr
														key={index}
														onClick={() =>
															document.type === "document"
																? handleDocumentEdit(document.id)
																: handleDocumentLinkEdit(document.id)
														}
													>
														<td>{document.title}</td>
														<td>
															<a
																href={document.url}
																style={{ color: "hsl(var(--action))" }}
																target="_blank"
																rel="noopener noreferrer"
																onClick={handleLinkClick}
															>
																{document.title}
															</a>

															{}
														</td>
														<td style={{ textTransform: "uppercase" }}>
															{document.type === "document"
																? t("DOCUMENT_TYPE_DOCUMENT")
																: t("DOCUMENT_TYPE_LINK")}
														</td>
														<td>
															<Button
																backgroundColor="danger"
																textColor="white"
																fontSize="s"
																loading={deleting}
																lessPadding
																onClick={(e) => {
																	e.stopPropagation();
																	handleDeleteDocument(document.id, document.title);
																}}
															>
																{t("DELETE")}
															</Button>
														</td>
													</tr>
												);
											}
										})}
								{!isLoading && documentsList?.documents?.length === 0 && (
									<tr>
										<td
											colSpan={4}
											style={{
												padding: "100px",
												color: "#888",
												fontStyle: "italic",
												textAlign: "center",
											}}
										>
											No data available
										</td>
									</tr>
								)}
								{isLoading && (
									<tr>
										<td colSpan={4}>
											<LoadingIndicator />
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</TableLayout>
					{((documentsList && documentsList?.totalCount) || 0) > PAGE_SIZE && (
						<Pagination
							currentPage={pageNumber}
							totalCount={documentsList?.totalCount || 0}
							onClickItem={(e) => {
								setPageNumber(e);
							}}
						/>
					)}
				</div>
			</div>
		</>
	);
};

export default DocumentsPage;
